import { useState, useRef, useCallback, useEffect } from 'react';
import { settingsApi } from '@/services/api';
import { DEFAULT_SITE_SETTINGS, SiteSettings, resolveUploadUrl } from '@/hooks/useCompanySettings';

interface Props {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export default function SiteSettingsTab({ onSuccess, onError }: Props) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [dbSettings, setDbSettings] = useState<Partial<SiteSettings>>({}); // Raw DB values
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [loadError, setLoadError] = useState<string>('');
  const [debugData, setDebugData] = useState<unknown>(null); // Debug data from API

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  // ── Load settings from DB via API ──────────────────────────────────────────
  useEffect(() => {
    setLoadError('');
    setLoading(true);
    
    // Add cache-busting timestamp to force fresh data
    const timestamp = Date.now();
    const originalFetch = window.fetch;
    
    settingsApi.getSiteSettings()
      .then((data: Partial<SiteSettings>) => {
        console.log('[SiteSettings] Raw data from DB:', JSON.stringify(data, null, 2));
        setDbSettings(data);
        
        // Use actual DB values, only fall back to defaults if key is completely missing
        const merged: SiteSettings = {
          site_logo_url: data.site_logo_url ?? DEFAULT_SITE_SETTINGS.site_logo_url,
          site_favicon_url: data.site_favicon_url ?? DEFAULT_SITE_SETTINGS.site_favicon_url,
          site_title: data.site_title ?? DEFAULT_SITE_SETTINGS.site_title,
          site_tagline: data.site_tagline ?? DEFAULT_SITE_SETTINGS.site_tagline,
          site_primary_color: data.site_primary_color ?? DEFAULT_SITE_SETTINGS.site_primary_color,
          site_date_format: data.site_date_format ?? DEFAULT_SITE_SETTINGS.site_date_format,
          site_sidebar_style: data.site_sidebar_style ?? DEFAULT_SITE_SETTINGS.site_sidebar_style,
        };
        
        console.log('[SiteSettings] Merged for display:', JSON.stringify(merged, null, 2));
        setSettings(merged);
        
        if (data.site_logo_url) {
          setLogoPreview(resolveUploadUrl(data.site_logo_url));
        }
        if (data.site_favicon_url) {
          setFaviconPreview(resolveUploadUrl(data.site_favicon_url));
        }
      })
      .catch((err) => {
        console.error('[SiteSettings] Load error:', err);
        setLoadError(err instanceof Error ? err.message : 'Failed to load settings');
        onError('Failed to load settings from database');
      })
      .finally(() => setLoading(false));
  }, [onError]);

  // ── Debug: Check raw DB values ───────────────────────────────────────────
  const checkDebugData = async () => {
    try {
      const data = await settingsApi.debugSiteSettings();
      setDebugData(data);
      console.log('[SiteSettings] Debug data:', data);
    } catch (err) {
      console.error('[SiteSettings] Debug error:', err);
    }
  };

  // ── Handle file selection & preview ────────────────────────────────────────
  const handleFileSelect = useCallback(
    (field: 'logo' | 'favicon', file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target?.result as string;
        if (field === 'logo') setLogoPreview(url);
        else setFaviconPreview(url);
      };
      reader.readAsDataURL(file);
    },
    []
  );

  // ── Upload file to backend (saves URL to DB) ────────────────────────────────
  const uploadFile = useCallback(
    async (field: 'logo' | 'favicon', file: File) => {
      const setUploading = field === 'logo' ? setUploadingLogo : setUploadingFavicon;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append(field, file);
        const result = await settingsApi.uploadBrandingFile(fd);
        const urlKey = field === 'logo' ? 'site_logo_url' : 'site_favicon_url';
        const newUrl = result.urls?.[urlKey] ?? '';
        setSettings((prev) => {
          const updated = { ...prev, [urlKey]: newUrl };
          // Notify other components
          window.dispatchEvent(new CustomEvent('site-settings-updated', { detail: updated }));
          return updated;
        });
        if (field === 'logo') setLogoPreview(newUrl);
        else setFaviconPreview(newUrl);
        onSuccess(`${field === 'logo' ? 'Logo' : 'Favicon'} uploaded and saved to database`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        onError(`Upload failed: ${msg}. Please check server connection.`);
      } finally {
        setUploading(false);
      }
    },
    [onSuccess, onError]
  );

  const handleFileChange = useCallback(
    (field: 'logo' | 'favicon') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      handleFileSelect(field, file);
      uploadFile(field, file);
      e.target.value = '';
    },
    [handleFileSelect, uploadFile]
  );

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const handleDrop = useCallback(
    (field: 'logo' | 'favicon') => (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      handleFileSelect(field, file);
      uploadFile(field, file);
    },
    [handleFileSelect, uploadFile]
  );

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  // ── Save all text settings to DB ────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      // Build payload with ALL current values (including empty strings)
      const settingsToSave: Record<string, string> = {
        site_title: settings.site_title || '',
        site_tagline: settings.site_tagline || '',
        site_primary_color: settings.site_primary_color || '#7c3aed',
        site_date_format: settings.site_date_format || 'DD/MM/YYYY',
        site_sidebar_style: settings.site_sidebar_style || 'dark',
        site_logo_url: settings.site_logo_url || '',
        site_favicon_url: settings.site_favicon_url || '',
      };
      
      console.log('[SiteSettings] Saving to DB:', JSON.stringify(settingsToSave, null, 2));
      
      const result = await settingsApi.updateSiteSettings(settingsToSave);
      console.log('[SiteSettings] Save response:', result);
      
      // Update the raw DB state to reflect what was saved
      setDbSettings(settingsToSave);
      
      // Notify other mounted components (e.g. ERPLayout sidebar)
      window.dispatchEvent(new CustomEvent('site-settings-updated', { detail: settings }));
      onSuccess('Site settings saved to database successfully');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save settings';
      console.error('[SiteSettings] Save error:', msg);
      if (msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('permission')) {
        onError('Permission denied: only Super Admin can update site settings.');
      } else if (msg.includes('401') || msg.toLowerCase().includes('unauthorized')) {
        onError('Session expired. Please log in again.');
      } else {
        onError(`Save failed: ${msg}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <i className="ri-loader-4-line text-xl animate-spin" />
          <span className="text-sm">Loading site settings from database...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-8">
        <div className="flex items-center gap-3 text-red-600">
          <i className="ri-error-warning-line text-xl" />
          <div>
            <p className="font-semibold">Failed to load settings</p>
            <p className="text-sm text-red-500">{loadError}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-3 text-sm px-4 py-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Branding Assets ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f5f3ff' }}>
            <i className="ri-image-line text-sm" style={{ color: '#7c3aed' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Branding Assets</h3>
            <p className="text-xs text-gray-500">Upload your site logo and favicon. Files are stored on the server.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo Upload */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">
              Site Logo
              <span className="ml-1 font-normal text-gray-400">(PNG / SVG, max 2 MB)</span>
            </label>
            <div
              className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all min-h-[140px]"
              onClick={() => logoInputRef.current?.click()}
              onDrop={handleDrop('logo')}
              onDragOver={handleDragOver}
            >
              {uploadingLogo ? (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#7c3aed' }} />
                  <span className="text-xs">Uploading to server...</span>
                </div>
              ) : logoPreview ? (
                <>
                  <img src={logoPreview} alt="Logo preview" className="max-h-16 max-w-full object-contain" />
                  <span className="text-xs text-gray-400">Click or drag to replace</span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-50">
                    <i className="ri-image-add-line text-xl text-gray-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Drop logo here</p>
                    <p className="text-xs text-gray-400">or click to browse</p>
                  </div>
                </>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.svg,.webp"
                className="hidden"
                onChange={handleFileChange('logo')}
              />
            </div>
            {settings.site_logo_url && (
              <p className="mt-1.5 text-xs text-gray-400 truncate">
                <i className="ri-database-2-line mr-1" />
                Stored in DB: {settings.site_logo_url}
              </p>
            )}
          </div>

          {/* Favicon Upload */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">
              Favicon
              <span className="ml-1 font-normal text-gray-400">(ICO / PNG 32×32, max 2 MB)</span>
            </label>
            <div
              className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-all min-h-[140px]"
              onClick={() => faviconInputRef.current?.click()}
              onDrop={handleDrop('favicon')}
              onDragOver={handleDragOver}
            >
              {uploadingFavicon ? (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#7c3aed' }} />
                  <span className="text-xs">Uploading to server...</span>
                </div>
              ) : faviconPreview ? (
                <>
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gray-50 border border-gray-100">
                    <img src={faviconPreview} alt="Favicon preview" className="w-10 h-10 object-contain" />
                  </div>
                  <span className="text-xs text-gray-400">Click or drag to replace</span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-50">
                    <i className="ri-bookmark-line text-xl text-gray-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Drop favicon here</p>
                    <p className="text-xs text-gray-400">or click to browse</p>
                  </div>
                </>
              )}
              <input
                ref={faviconInputRef}
                type="file"
                accept=".ico,.png,.jpg,.jpeg,.gif,.svg,.webp"
                className="hidden"
                onChange={handleFileChange('favicon')}
              />
            </div>
            {settings.site_favicon_url && (
              <p className="mt-1.5 text-xs text-gray-400 truncate">
                <i className="ri-database-2-line mr-1" />
                Stored in DB: {settings.site_favicon_url}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Site Identity ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f5f3ff' }}>
            <i className="ri-global-line text-sm" style={{ color: '#7c3aed' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Site Identity</h3>
            <p className="text-xs text-gray-500">Name and tagline shown in the browser tab and login screen.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Site Title</label>
            <input
              type="text"
              value={settings.site_title}
              onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
              placeholder="SurveyPro ERP"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Tagline</label>
            <input
              type="text"
              value={settings.site_tagline}
              onChange={(e) => setSettings({ ...settings, site_tagline: e.target.value })}
              placeholder="Survey & CAD Management System"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
            />
          </div>
        </div>
      </div>

      {/* ── Appearance Preferences ───────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f5f3ff' }}>
            <i className="ri-palette-line text-sm" style={{ color: '#7c3aed' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Appearance Preferences</h3>
            <p className="text-xs text-gray-500">Primary color, sidebar style, and date format.</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Primary Color */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Primary Color</label>
            <div className="flex flex-wrap gap-2 items-center">
              {[
                { label: 'Violet', value: '#7c3aed' },
                { label: 'Emerald', value: '#10b981' },
                { label: 'Rose', value: '#f43f5e' },
                { label: 'Amber', value: '#f59e0b' },
                { label: 'Slate', value: '#475569' },
              ].map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setSettings({ ...settings, site_primary_color: c.value })}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    settings.site_primary_color === c.value
                      ? 'border-violet-400 bg-violet-50 text-violet-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ background: c.value }} />
                  {c.label}
                </button>
              ))}
              <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2">
                <span className="text-xs text-gray-500">Custom:</span>
                <input
                  type="color"
                  value={settings.site_primary_color}
                  onChange={(e) => setSettings({ ...settings, site_primary_color: e.target.value })}
                  className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
                />
                <span className="text-xs font-mono text-gray-600">{settings.site_primary_color}</span>
              </div>
            </div>
          </div>

          {/* Sidebar Style */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Sidebar Style</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'dark', label: 'Dark', icon: 'ri-moon-line' },
                { value: 'light', label: 'Light', icon: 'ri-sun-line' },
                { value: 'colored', label: 'Colored', icon: 'ri-palette-line' },
              ].map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSettings({ ...settings, site_sidebar_style: s.value })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    settings.site_sidebar_style === s.value
                      ? 'border-violet-400 bg-violet-50 text-violet-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <i className={s.icon} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date Format */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Date Format</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (Indian)' },
                { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
                { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
              ].map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setSettings({ ...settings, site_date_format: f.value })}
                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    settings.site_date_format === f.value
                      ? 'border-violet-400 bg-violet-50 text-violet-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Live Preview ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f5f3ff' }}>
            <i className="ri-eye-line text-sm" style={{ color: '#7c3aed' }} />
          </div>
          <h3 className="font-bold text-gray-900 text-sm">Live Preview</h3>
        </div>

        {/* Browser tab mockup */}
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b border-gray-200">
            <div className="flex gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="w-3 h-3 rounded-full bg-yellow-400" />
              <span className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 bg-white rounded-md px-3 py-1 flex items-center gap-2 text-xs text-gray-500 border border-gray-200">
              {faviconPreview ? (
                <img src={faviconPreview} alt="favicon" className="w-3.5 h-3.5 object-contain" />
              ) : (
                <i className="ri-bookmark-line text-gray-400" style={{ fontSize: '12px' }} />
              )}
              <span className="truncate">{settings.site_title || 'SurveyPro ERP'}</span>
            </div>
          </div>
          <div className="flex" style={{ height: '100px' }}>
            {/* Sidebar preview */}
            <div
              className="w-36 flex-shrink-0 p-3 flex flex-col gap-2"
              style={{
                background: settings.site_sidebar_style === 'light'
                  ? '#f8fafc'
                  : settings.site_sidebar_style === 'colored'
                  ? settings.site_primary_color
                  : '#1e1b4b',
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" className="h-5 object-contain max-w-[80px]" />
                ) : (
                  <span
                    className="text-xs font-bold truncate"
                    style={{
                      color: settings.site_sidebar_style === 'light' ? '#374151' : '#fff',
                    }}
                  >
                    {settings.site_title || 'SurveyPro'}
                  </span>
                )}
              </div>
              {['Dashboard', 'Job Cards', 'Clients'].map((item) => (
                <div
                  key={item}
                  className="text-xs px-2 py-1 rounded-md"
                  style={{
                    color: settings.site_sidebar_style === 'light' ? '#6b7280' : 'rgba(255,255,255,0.7)',
                    background: 'transparent',
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
            {/* Content area */}
            <div className="flex-1 bg-gray-50 p-3">
              <div className="text-xs font-bold mb-1" style={{ color: settings.site_primary_color }}>
                {settings.site_title}
              </div>
              <div className="text-xs text-gray-400">{settings.site_tagline}</div>
              <div className="mt-2 flex gap-1.5">
                <div
                  className="text-xs text-white px-2 py-0.5 rounded-md"
                  style={{ background: settings.site_primary_color }}
                >
                  Primary Button
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Debug Panel ───────────────────────────────────────────────────────── */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
            <i className="ri-bug-line" />
            Debug: Database Inspector
          </p>
          <button
            onClick={checkDebugData}
            className="text-xs px-3 py-1 bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 transition-colors"
          >
            Check DB
          </button>
        </div>
        
        {Object.keys(dbSettings).length > 0 && (
          <>
            <p className="text-xs text-gray-400 mb-1">API Response (site settings):</p>
            <pre className="text-xs text-gray-600 bg-white p-3 rounded-lg overflow-auto max-h-32 mb-3">
              {JSON.stringify(dbSettings, null, 2)}
            </pre>
          </>
        )}
        
        {debugData && (
          <>
            <p className="text-xs text-gray-400 mb-1">Raw DB Query (debug endpoint):</p>
            <pre className="text-xs text-gray-600 bg-white p-3 rounded-lg overflow-auto max-h-40">
              {JSON.stringify(debugData, null, 2)}
            </pre>
          </>
        )}
      </div>

      {/* ── Save Button ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <i className="ri-database-2-line" />
          All settings are saved directly to MariaDB — no browser storage used.
        </p>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 text-white px-6 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
          style={{ background: '#7c3aed' }}
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Saving to Database...
            </>
          ) : (
            <>
              <i className="ri-database-2-line" />
              Save to Database
            </>
          )}
        </button>
      </div>
    </div>
  );
}
